import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Upload, message, Alert } from 'antd';
import { InboxOutlined, FileImageOutlined, ArrowLeftOutlined, PlayCircleOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';

import { useResponsive } from '../../hooks/useResponsive';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { IAssessmentsState } from '../../store/reducers/assessments/types';
import { IClassesState } from '../../store/reducers/classes/types';
import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';
import { URLs } from '../../store/urls';

const { Dragger } = Upload;

// Styled Components
const PageWrapper = styled.div`
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BackButton = styled(BusinessButton)`
  flex-shrink: 0;
`;

const AssessmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1677ff;
  flex: 1;
  justify-content: flex-end;
  
  .label {
    color: #666;
    font-weight: 500;
  }
  
  .name {
    color: #1677ff;
    font-weight: 700;
  }
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    width: 100%;
  }
`;

const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  &::before {
    content: '📄';
    font-size: 36px;
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::before {
      font-size: 28px;
    }
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;


const UploadSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const StyledDragger = styled(Dragger)`
  &.ant-upload-wrapper {
    .ant-upload-drag {
      border: 2px dashed rgba(22, 119, 255, 0.3);
      border-radius: 16px;
      background: rgba(22, 119, 255, 0.02);
      padding: 60px 40px;
      transition: all 0.3s ease;
      
      &:hover {
        border-color: #1677ff;
        background: rgba(22, 119, 255, 0.05);
      }
      
      @media (max-width: 768px) {
        padding: 40px 20px;
      }
    }
    
    .ant-upload-drag-icon {
      margin-bottom: 20px;
      
      .anticon {
        font-size: 64px;
        color: #1677ff;
        
        @media (max-width: 768px) {
          font-size: 48px;
        }
      }
    }
    
    .ant-upload-text {
      font-size: 18px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 8px;
      
      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
    
    .ant-upload-hint {
      font-size: 14px;
      color: #666;
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
  }
`;

const SupportedFormats = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FormatLabel = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 500;
`;

const FormatBadge = styled.span`
  padding: 6px 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1677ff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:not(:last-child) {
    margin-right: 8px;
  }
`;

const ValidationSection = styled.div`
  margin-top: 32px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ValidationSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ValidationCard = styled.div<{ variant?: 'info' | 'success' | 'warning' | 'error' }>`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid;
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background: rgba(34, 197, 94, 0.05);
          border-color: rgba(34, 197, 94, 0.2);
        `;
      case 'warning':
        return `
          background: rgba(245, 158, 11, 0.05);
          border-color: rgba(245, 158, 11, 0.2);
        `;
      case 'error':
        return `
          background: rgba(239, 68, 68, 0.05);
          border-color: rgba(239, 68, 68, 0.2);
        `;
      default:
        return `
          background: rgba(22, 119, 255, 0.05);
          border-color: rgba(22, 119, 255, 0.2);
        `;
    }
  }}
  
  .card-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }
  
  .card-value {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
  }
`;

const ErrorsList = styled.div`
  margin-top: 16px;
`;

const ErrorItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  margin-bottom: 8px;
  
  .error-icon {
    font-size: 18px;
    color: #ef4444;
    flex-shrink: 0;
  }
  
  .error-content {
    flex: 1;
    
    .error-file {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 2px;
    }
    
    .error-message {
      font-size: 13px;
      color: #ef4444;
    }
  }
`;

const WarningItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(245, 158, 11, 0.05);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
  margin-bottom: 8px;
  
  .warning-icon {
    font-size: 18px;
    color: #f59e0b;
    flex-shrink: 0;
  }
  
  .warning-content {
    flex: 1;
    
    .warning-file {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 2px;
    }
    
    .warning-message {
      font-size: 13px;
      color: #d97706;
    }
  }
`;

const StartGradingButton = styled(BusinessButton)`
  margin-top: 24px;
  min-width: 200px;
`;

const UploadAnswerSheetsPage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get assessment ID from URL
  const assessmentId = searchParams.get('assessmentId');
  const assessmentName = searchParams.get('name');
  
  // Redux
  const { assessments, loading } = useTypedSelector<IAssessmentsState>(state => state.assessments);
  const { classes } = useTypedSelector<IClassesState>(state => state.classes);
  const { getAssessmentsRequest, getClassesRequest } = useActions();
  
  // Local state
  const [fileList, setFileList] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [totalStudents, setTotalStudents] = useState<number>(0);
  const [loadingStudentCount, setLoadingStudentCount] = useState(false);
  const [validationResults, setValidationResults] = useState<{
    totalFiles: number;
    validFiles: number;
    totalSize: number;
    duplicates: string[];
    errors: { fileName: string; error: string }[];
  }>({
    totalFiles: 0,
    validFiles: 0,
    totalSize: 0,
    duplicates: [],
    errors: []
  });

  // Find current assessment
  const assessment: any = assessments?.find((a: any) => a.id === Number(assessmentId));

  // Validation function
  const validateFiles = (files: any[]) => {
    const errors: { fileName: string; error: string }[] = [];
    const duplicates: string[] = [];
    const fileNames = new Set<string>();
    let totalSize = 0;
    let validFiles = 0;

    files.forEach((file) => {
      const fileName = file.name;
      
      // Check for duplicates
      if (fileNames.has(fileName)) {
        duplicates.push(fileName);
      }
      fileNames.add(fileName);

      // Validate file type
      const isValidType = file.type === 'application/pdf' || 
                          file.type === 'image/png' || 
                          file.type === 'image/jpeg';
      
      if (!isValidType) {
        errors.push({ fileName, error: 'Unsupported format' });
      }

      // Validate file size (max 10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        errors.push({ fileName, error: 'File size exceeds 10MB' });
      }

      // Simulate quality check (would be done on server)
      if (file.size < 50000) { // Less than 50KB might be low quality
        errors.push({ fileName, error: 'Low quality scan (file too small)' });
      }

      // Calculate total size
      totalSize += file.size;

      // Count valid files
      if (isValidType && isLt10M && file.size >= 50000) {
        validFiles++;
      }
    });

    setValidationResults({
      totalFiles: files.length,
      validFiles,
      totalSize,
      duplicates,
      errors
    });
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    // No accept filter - show all files in file picker
    fileList: fileList,
    showUploadList: false, // Hide the default file list
    beforeUpload: () => false, // Prevent auto upload
    onChange(info) {
      setFileList(info.fileList);
      validateFiles(info.fileList);
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  useEffect(() => {
    // Load assessments and classes if needed
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      getAssessmentsRequest();
      getClassesRequest();
    }
  }, []);

  // Fetch student count when assessment is loaded
  useEffect(() => {
    const fetchStudentCount = async () => {
      if (!assessment || !assessment.classIds || assessment.classIds.length === 0) {
        setTotalStudents(0);
        return;
      }

      setLoadingStudentCount(true);
      
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch(
          `${URLs.BASE_URL}${URLs.GET_STUDENTS_COUNT_ENDPOINT}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              classIds: assessment.classIds
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch student count: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          console.log('[UploadAnswerSheets] Student count from API:', result.data.totalStudents);
          setTotalStudents(result.data.totalStudents || 0);
        } else {
          console.warn('[UploadAnswerSheets] Invalid response from students count API');
          setTotalStudents(0);
        }
        
      } catch (error: any) {
        console.error('[UploadAnswerSheets] Error fetching student count:', error);
        setTotalStudents(0);
      } finally {
        setLoadingStudentCount(false);
      }
    };

    fetchStudentCount();
  }, [assessment]);

  const handleBack = () => {
    navigate('/add-to-grading');
  };

  const handleStartGrading = async () => {
    // Validate errors
    if (validationResults.errors.length > 0) {
      notificationController.error({
        message: 'Cannot Start Grading',
        description: 'Please fix all errors before starting grading process.'
      });
      return;
    }

    // Validate file count matches student count
    if (validationResults.validFiles !== totalStudents) {
      const message = validationResults.validFiles < totalStudents
        ? `Not enough files. Expected ${totalStudents} files (one per student), but got ${validationResults.validFiles}.`
        : `Too many files. Expected ${totalStudents} files (one per student), but got ${validationResults.validFiles}.`;
      
      notificationController.error({
        message: 'File Count Mismatch',
        description: message
      });
      return;
    }

    // Prepare FormData for upload
    const formData = new FormData();
    
    // Add valid files to FormData with the correct field name expected by backend
    fileList.forEach((file) => {
      // Only add valid files (no errors for this file)
      const hasError = validationResults.errors.some(err => err.fileName === file.name);
      if (!hasError) {
        // Backend expects 'files' field name (matching List<IFormFile> files parameter)
        formData.append('files', file.originFileObj || file);
      }
    });

    setIsUploading(true);

    try {
      // Get auth token
      const token = localStorage.getItem('token');
      
      console.log('[Upload] Starting upload for assessmentId:', assessmentId);
      console.log('[Upload] Files count:', validationResults.validFiles);
      console.log('[Upload] FormData files:', Array.from(formData.entries()).map(([key, value]) => ({ key, value })));
      
      // Send to server
      // Note: Don't set Content-Type header - browser will set it automatically with boundary
      const response = await fetch(
        `${URLs.BASE_URL}${URLs.CREATE_BATCH_RECOGNITION}/${assessmentId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        }
      );

      console.log('[Upload] Response status:', response.status, response.statusText);
      
      // Parse response
      const result = await response.json();
      console.log('[Upload] Response result:', result);

      // Check if response indicates success
      if (!response.ok || !result.success) {
        const errorMessage = result.message || result.error || response.statusText || 'Upload failed';
        throw new Error(errorMessage);
      }
      
      // Success - show notification and navigate
      notificationController.success({
        message: 'Upload Successful',
        description: result.message || `Successfully uploaded ${validationResults.validFiles} file${validationResults.validFiles !== 1 ? 's' : ''}. Processing started.`
      });

      // Navigate back to Add to Grading page
      setTimeout(() => {
        navigate('/add-to-grading');
      }, 1000);
      
    } catch (error: any) {
      console.error('[Upload] Error:', error);
      
      // Show error notification with detailed message
      notificationController.error({
        message: 'Upload Failed',
        description: error.message || 'Failed to upload answer sheets. Please try again.',
        duration: 6 // Show error for 6 seconds
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <PageTitle 
        title="Upload Answer Sheets" 
        description="Upload scanned answer sheets for grading" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        <ContentCard>
          <PageWrapper>
            <HeaderBar>
              <BackButton
                variant="ghost"
                businessSize="small"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
              >
                Back to Assessments
              </BackButton>
              
              {assessmentName && (
                <AssessmentInfo>
                  <span className="label">📝 Selected Assessment:</span>
                  <span className="name">{decodeURIComponent(assessmentName)}</span>
                </AssessmentInfo>
              )}
            </HeaderBar>

            <TitleSection>
              <Title>Upload OMR Sheets</Title>
              <Subtitle>Upload scanned answer sheets to process for grading</Subtitle>
              {assessmentId && !assessment && !loading && (
                <div style={{ marginTop: 16 }}>
                  <Alert 
                    type="warning" 
                    message="Assessment not found" 
                    description="Unable to load assessment details. Please go back and try again."
                    showIcon 
                  />
                </div>
              )}
              {assessment && totalStudents === 0 && (
                <div style={{ marginTop: 16 }}>
                  <Alert 
                    type="info" 
                    message="No students found" 
                    description="This assessment has no students enrolled. Please add students to the class first."
                    showIcon 
                  />
                </div>
              )}
            </TitleSection>

            <UploadSection>
              <StyledDragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Drag & drop scanned answer sheets here
                </p>
                <p className="ant-upload-hint">
                  or click to upload • Multiple files supported
                </p>
              </StyledDragger>
              
              <SupportedFormats>
                <FormatLabel>Supported formats:</FormatLabel>
                <div>
                  <FormatBadge>
                    <FileImageOutlined />
                    PDF
                  </FormatBadge>
                  <FormatBadge>
                    <FileImageOutlined />
                    PNG
                  </FormatBadge>
                </div>
                <FormatLabel style={{ marginLeft: 8, color: '#1677ff' }}>
                  • Multiple files
                </FormatLabel>
              </SupportedFormats>
            </UploadSection>

            {fileList.length > 0 && (
              <>
                <ValidationSection>
                  <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
                    📊 Validation Results
                  </h3>
                  
                  <ValidationSummary>
                    <ValidationCard variant="info">
                      <div className="card-label">Total Files</div>
                      <div className="card-value">{validationResults.totalFiles}</div>
                    </ValidationCard>
                    
                    <ValidationCard variant={validationResults.validFiles === validationResults.totalFiles ? 'success' : 'warning'}>
                      <div className="card-label">Valid Files</div>
                      <div className="card-value">{validationResults.validFiles}</div>
                    </ValidationCard>
                    
                    <ValidationCard variant="info">
                      <div className="card-label">Total Size</div>
                      <div className="card-value">
                        {(validationResults.totalSize / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </ValidationCard>
                    
                    <ValidationCard variant={validationResults.errors.length > 0 ? 'error' : 'success'}>
                      <div className="card-label">Errors</div>
                      <div className="card-value">{validationResults.errors.length}</div>
                    </ValidationCard>
                  </ValidationSummary>

                  {/* Student count validation alert */}
                  {totalStudents === 0 ? (
                    <Alert
                      type="error"
                      message="Cannot Start Grading"
                      description="This assessment has no students enrolled. Please add students to the class before uploading answer sheets."
                      showIcon
                      style={{ marginTop: 16, marginBottom: 16 }}
                    />
                  ) : totalStudents > 0 && validationResults.validFiles !== totalStudents ? (
                    <Alert
                      type={validationResults.totalFiles === 0 ? 'info' : 'warning'}
                      message="File Count Mismatch"
                      description={
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <strong>Expected:</strong> {totalStudents} files (one per student)
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <strong>Uploaded:</strong> {validationResults.totalFiles} files
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <strong>Valid:</strong> {validationResults.validFiles} files
                          </div>
                          <div>
                            {validationResults.errors.length > 0 && (
                              <span>⚠️ Fix {validationResults.errors.length} error(s) first. </span>
                            )}
                            {validationResults.validFiles < totalStudents ? (
                              <span>
                                Then upload {totalStudents - validationResults.validFiles} more file(s) to match {totalStudents} students.
                              </span>
                            ) : validationResults.validFiles > totalStudents ? (
                              <span>
                                Remove {validationResults.validFiles - totalStudents} file(s) to match {totalStudents} students.
                              </span>
                            ) : null}
                          </div>
                        </>
                      }
                      showIcon
                      style={{ marginTop: 16, marginBottom: 16 }}
                    />
                  ) : null}

                  {validationResults.duplicates.length > 0 && (
                    <ErrorsList>
                      <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#f59e0b' }}>
                        ⚠️ Duplicate Files
                      </h4>
                      {validationResults.duplicates.map((fileName, index) => (
                        <WarningItem key={index}>
                          <span className="warning-icon">⚠️</span>
                          <div className="warning-content">
                            <div className="warning-file">{fileName}</div>
                            <div className="warning-message">Duplicate file detected</div>
                          </div>
                        </WarningItem>
                      ))}
                    </ErrorsList>
                  )}

                  {validationResults.errors.length > 0 && (
                    <ErrorsList>
                      <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#ef4444' }}>
                        ❌ Errors Found
                      </h4>
                      {validationResults.errors.map((error, index) => (
                        <ErrorItem key={index}>
                          <span className="error-icon">❌</span>
                          <div className="error-content">
                            <div className="error-file">{error.fileName}</div>
                            <div className="error-message">{error.error}</div>
                          </div>
                        </ErrorItem>
                      ))}
                    </ErrorsList>
                  )}

                  {/* Start Grading Button - only if no errors and correct file count */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                    <StartGradingButton
                      variant="primary"
                      businessSize="large"
                      icon={<PlayCircleOutlined />}
                      onClick={handleStartGrading}
                      disabled={
                        validationResults.errors.length > 0 || 
                        validationResults.validFiles === 0 || 
                        validationResults.validFiles !== totalStudents ||
                        isUploading
                      }
                      loading={isUploading}
                    >
                      {isUploading ? 'Uploading...' : 'Start Grading'}
                    </StartGradingButton>
                  </div>

                  {/* Explanation why button is disabled */}
                  {(validationResults.errors.length > 0 || validationResults.validFiles !== totalStudents) && validationResults.totalFiles > 0 && (
                    <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: '#666', lineHeight: 1.6 }}>
                      {validationResults.errors.length > 0 && (
                        <div>❌ Fix {validationResults.errors.length} error(s) in uploaded files.</div>
                      )}
                      {validationResults.validFiles !== totalStudents && (
                        <div>
                          ⚠️ {validationResults.validFiles} valid file(s) uploaded, but {totalStudents} required (one per student).
                          {validationResults.validFiles < totalStudents 
                            ? ` Upload ${totalStudents - validationResults.validFiles} more.`
                            : ` Remove ${validationResults.validFiles - totalStudents} file(s).`
                          }
                        </div>
                      )}
                    </div>
                  )}
                </ValidationSection>
              </>
            )}
          </PageWrapper>
        </ContentCard>
      </PageContainer>
    </>
  );
};

export default UploadAnswerSheetsPage;

